import React, { useState, useEffect } from 'react'
import SupportMerchantApiDto from '../../../../../api-dtos/support/merchant/support-merchant-api.dto.interface'
import BusinessInformation from '../business-information/business-information.component'
import MCCSelector from '../mcc-selector/mcc-selector.component'
import AddressInformation from '../address-information/address-information.component'
import StripeIntegration from '../stripe-integration/stripe-integration.component'
import SaveButton from '../save-button/save-button.component'
import { supportUpdateMerchantDetails } from '../../../../../helpers/lopay_api_helper'
import { MerchantFormData, SelectedMCC } from '../../types'
import { useFormValidation } from '../../hooks'

type MerchantDetailsProps = {
  merchant: SupportMerchantApiDto
  syncWithStripeFn: () => Promise<void>
  isSyncingWithStripe: boolean
  onMerchantUpdated?: () => Promise<void>
}

const MerchantDetails = (props: MerchantDetailsProps) => {
  const { merchant, syncWithStripeFn, isSyncingWithStripe, onMerchantUpdated } =
    props

  // Form state
  const [formData, setFormData] = useState<MerchantFormData>({
    name: merchant.name || '',
    email: merchant.email || '',
    website: merchant.website || '',
    telephone: merchant.telephone || '',
    statementDescriptor: merchant.statementDescriptor || '',
    selectedMCC: {
      code: merchant.category?.code ? parseInt(merchant.category.code) : null,
      name: merchant.category?.name || '',
      categoryName: '', // We'll need to determine this from the API data
    },
    address: {
      nameOrNumber: merchant.address?.nameOrNumber || '',
      street: merchant.address?.street || '',
      line2: merchant.address?.line2 || '',
      state: merchant.address?.state || '',
      town: merchant.address?.town || '',
      postcode: merchant.address?.postcode || '',
    },
  })

  // Track if form has changes
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Use validation hook
  const { errors, validateField, validateAllFormFields, hasErrors } =
    useFormValidation()

  // Reset form data when merchant prop changes (after save/refresh)
  useEffect(() => {
    setFormData({
      name: merchant.name || '',
      email: merchant.email || '',
      website: merchant.website || '',
      telephone: merchant.telephone || '',
      statementDescriptor: merchant.statementDescriptor || '',
      selectedMCC: {
        code: merchant.category?.code ? parseInt(merchant.category.code) : null,
        name: merchant.category?.name || '',
        categoryName: '', // We'll need to determine this from the API data
      },
      address: {
        nameOrNumber: merchant.address?.nameOrNumber || '',
        street: merchant.address?.street || '',
        line2: merchant.address?.line2 || '',
        state: merchant.address?.state || '',
        town: merchant.address?.town || '',
        postcode: merchant.address?.postcode || '',
      },
    })
  }, [merchant])

  // Check for changes whenever formData updates
  useEffect(() => {
    const originalMCCCode = merchant.category?.code
      ? parseInt(merchant.category.code)
      : null
    const hasFormChanges =
      formData.name !== (merchant.name || '') ||
      formData.email !== (merchant.email || '') ||
      formData.website !== (merchant.website || '') ||
      formData.telephone !== (merchant.telephone || '') ||
      formData.statementDescriptor !== (merchant.statementDescriptor || '') ||
      formData.selectedMCC.code !== originalMCCCode ||
      formData.address.nameOrNumber !==
        (merchant.address?.nameOrNumber || '') ||
      formData.address.street !== (merchant.address?.street || '') ||
      formData.address.line2 !== (merchant.address?.line2 || '') ||
      formData.address.state !== (merchant.address?.state || '') ||
      formData.address.town !== (merchant.address?.town || '') ||
      formData.address.postcode !== (merchant.address?.postcode || '')

    setHasChanges(hasFormChanges)
  }, [formData, merchant])

  const handleInputChange = (field: string, value: string) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1]
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }))

      // Validate fields in real-time
      if (
        [
          'name',
          'email',
          'website',
          'telephone',
          'statementDescriptor',
        ].includes(field)
      ) {
        validateField(field as keyof typeof errors, value)
      }
    }
  }

  const handleMCCChange = (selectedMCC: SelectedMCC) => {
    setFormData((prev) => ({
      ...prev,
      selectedMCC,
    }))
  }

  const handleSaveChanges = async () => {
    // Validate all fields before saving
    const validationErrors = validateAllFormFields(formData)

    // Check if any validation errors exist
    if (Object.values(validationErrors).some((error) => error !== '')) {
      return
    }

    setIsSaving(true)
    try {
      // Build request body with only changed fields
      const requestBody: any = {}

      // Check each field and only include if changed
      const originalMCCCode = merchant.category?.code
        ? parseInt(merchant.category.code)
        : null

      if (formData.name !== (merchant.name || '')) {
        requestBody.name = formData.name
      }

      if (formData.email !== (merchant.email || '')) {
        requestBody.email = formData.email
      }

      if (formData.website !== (merchant.website || '')) {
        requestBody.website = formData.website || undefined
      }

      if (formData.telephone !== (merchant.telephone || '')) {
        requestBody.telephone = formData.telephone || undefined
      }

      if (
        formData.statementDescriptor !== (merchant.statementDescriptor || '')
      ) {
        requestBody.statementDescriptor =
          formData.statementDescriptor || undefined
      }

      if (formData.selectedMCC.code !== originalMCCCode) {
        requestBody.mcc = formData.selectedMCC.code || undefined
      }

      // Check address fields
      const addressChanges: any = {}
      let hasAddressChanges = false

      if (
        formData.address.nameOrNumber !== (merchant.address?.nameOrNumber || '')
      ) {
        addressChanges.nameOrNumber = formData.address.nameOrNumber || undefined
        hasAddressChanges = true
      }

      if (formData.address.street !== (merchant.address?.street || '')) {
        addressChanges.street = formData.address.street
        hasAddressChanges = true
      }

      if (formData.address.line2 !== (merchant.address?.line2 || '')) {
        addressChanges.line2 = formData.address.line2 || undefined
        hasAddressChanges = true
      }

      if (formData.address.state !== (merchant.address?.state || '')) {
        addressChanges.state = formData.address.state || undefined
        hasAddressChanges = true
      }

      if (formData.address.town !== (merchant.address?.town || '')) {
        addressChanges.town = formData.address.town
        hasAddressChanges = true
      }

      if (formData.address.postcode !== (merchant.address?.postcode || '')) {
        addressChanges.postcode = formData.address.postcode
        hasAddressChanges = true
      }

      if (hasAddressChanges) {
        requestBody.address = addressChanges
      }

      // Remove undefined values to keep the request clean
      const cleanedRequestBody = JSON.parse(JSON.stringify(requestBody))

      // Make the API call
      await supportUpdateMerchantDetails(merchant.id, cleanedRequestBody)

      setHasChanges(false)

      // Notify parent component to refresh merchant data
      if (onMerchantUpdated) {
        await onMerchantUpdated()
      }
    } catch (error) {
      console.error('Error saving merchant details:', error)
      // TODO: Add proper error handling/notification to user
    } finally {
      setIsSaving(false)
    }
  }

  const isFieldDisabled = isSaving

  return (
    <>
      {/* Business Information Section */}
      <BusinessInformation
        name={formData.name}
        email={formData.email}
        website={formData.website}
        telephone={formData.telephone}
        statementDescriptor={formData.statementDescriptor}
        nameError={errors.name}
        emailError={errors.email}
        websiteError={errors.website}
        telephoneError={errors.telephone}
        statementDescriptorError={errors.statementDescriptor}
        onFieldChange={handleInputChange}
        disabled={isFieldDisabled}
      />

      {/* Merchant Category Code (MCC) Section */}
      <MCCSelector
        selectedMCC={formData.selectedMCC}
        onMCCChange={handleMCCChange}
        disabled={isFieldDisabled}
      />

      {/* Address Information Section */}
      <AddressInformation
        address={formData.address}
        onFieldChange={handleInputChange}
        disabled={isFieldDisabled}
      />

      {/* Stripe Integration Section */}
      <StripeIntegration
        onSyncWithStripe={syncWithStripeFn}
        isSyncing={isSyncingWithStripe}
      />

      {/* Save Button */}
      <SaveButton
        hasChanges={hasChanges}
        isSaving={isSaving}
        hasErrors={hasErrors()}
        onSave={handleSaveChanges}
      />
    </>
  )
}

export default MerchantDetails
